import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { api } from '@udm/contracts';
import { Building2, KeyRound, Search, ShieldCheck, UserPlus, Users as UsersIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUser, deleteUser, fetchRoles, fetchUsers, isClientUser, updateUser } from '../../api/client';
import { useMe } from '../auth/session';
import { Card, Drawer, Field, PageHeader, Segmented } from '../../ui/components';
import { fetchProjects } from '../../api/ops';
import { formatNumber, formatRelative } from '../../ui/format';
import { ErrorNote } from '../admin/ErrorNote';
import { MobilePairing } from './MobilePairing';
import { useAdminSites } from '../sites/sitesData';
import './users.css';

type User = api.User;
type Role = api.Role;
type Site = api.Site;

const USERS_KEY = ['admin', 'users'] as const;
/** Client roles in display order; their badge colours: administrator orange, operator grey, observer green. */
const CLIENT_ROLE_ORDER = ['SITE_ADMIN', 'OPERATOR', 'AUTHORITY_OBSERVER'];
const ROLES_KEY = ['admin', 'roles'] as const;

/** Visible sites in tree order with their depth, and each site's client (root of the visible tree). */
function useSiteTree() {
  const sites = useAdminSites();
  return useMemo(() => {
    const items = sites.data?.items ?? [];
    const byId = new Map(items.map((s) => [s.id, s]));
    const kids = new Map<string | null, Site[]>();
    for (const s of items) {
      const k = s.parent_id && byId.has(s.parent_id) ? s.parent_id : null;
      kids.set(k, [...(kids.get(k) ?? []), s]);
    }
    const ordered: { site: Site; depth: number; root: Site }[] = [];
    const walk = (parent: string | null, depth: number, root?: Site) =>
      (kids.get(parent) ?? [])
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((s) => {
          ordered.push({ site: s, depth, root: root ?? s });
          walk(s.id, depth + 1, root ?? s);
        });
    walk(null, 0);
    const rootOf = new Map(ordered.map((o) => [o.site.id, o.root]));
    return { ordered, rootOf, roots: ordered.filter((o) => o.depth === 0).map((o) => o.site) };
  }, [sites.data]);
}

export function UsersPage() {
  const { t } = useTranslation();
  const users = useQuery({ queryKey: USERS_KEY, queryFn: fetchUsers });
  const roles = useQuery({ queryKey: ROLES_KEY, queryFn: fetchRoles, staleTime: 300_000 });
  const tree = useSiteTree();
  const [drawer, setDrawer] = useState<{ id: string; created?: true } | 'new' | null>(null);
  const [search, setSearch] = useState('');
  const [client, setClient] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'disabled'>('all');
  // client accounts first, platform accounts (global roles) in their own tab (user request 2026-10-22)
  const [tab, setTab] = useState<'clients' | 'platform'>('clients');
  const canCreate = (roles.data?.items ?? []).some((r) => r.grantable);
  const clientUser = isClientUser(useMe().data);

  const all = useMemo(() => users.data?.items ?? [], [users.data]);
  const platform = (u: User) => u.assignments.length === 0 || u.assignments.some((a) => a.site_id === null);
  const counts = useMemo(
    () => ({ platform: all.filter(platform).length, clients: all.filter((u) => !platform(u)).length }),
    [all],
  );
  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter(
      (u) =>
        (clientUser || (tab === 'platform') === platform(u)) &&
        (status === 'all' || u.status === status) &&
        (!q ||
          u.username.includes(q) ||
          u.display_name.toLowerCase().includes(q) ||
          (u.email ?? '').toLowerCase().includes(q)) &&
        (tab === 'platform' ||
          !client ||
          u.assignments.some((a) => a.site_id && tree.rootOf.get(a.site_id)?.id === client)),
    );
  }, [all, search, status, client, tree, tab, clientUser]);
  // client tab: one group per client, users by role (administrator, operator, observer) then name
  const groups = useMemo(() => {
    const byClient = new Map<string, { client: Site | undefined; name: string; users: User[] }>();
    for (const u of list) {
      const a = u.assignments.find((x) => x.site_id);
      const root = a?.site_id ? tree.rootOf.get(a.site_id) : undefined;
      const key = root?.id ?? a?.site_id ?? '';
      const g = byClient.get(key) ?? { client: root, name: root?.name ?? a?.site_name ?? '—', users: [] };
      g.users.push(u);
      byClient.set(key, g);
    }
    const order = (role: string) => {
      const i = CLIENT_ROLE_ORDER.indexOf(role);
      return i < 0 ? CLIENT_ROLE_ORDER.length : i;
    };
    const rank = (u: User) => Math.min(...u.assignments.map((a) => order(a.role)));
    return [...byClient.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((g) => ({
        ...g,
        users: g.users.sort((a, b) => rank(a) - rank(b) || a.display_name.localeCompare(b.display_name)),
      }));
  }, [list, tree]);
  const userRow = (u: User, withClient: boolean) => (
    <tr
      key={u.id}
      className={`row-click${selected?.id === u.id ? ' row-selected' : ''}`}
      onClick={() => setDrawer({ id: u.id })}
    >
      <td>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
          <strong>{u.display_name}</strong>
          <span className="mono muted" style={{ fontSize: 'var(--fs-xs)' }}>
            {u.username}
          </span>
        </div>
      </td>
      <td>
        <AssignmentChips user={u} withClient={withClient} />
      </td>
      <td>
        <span className={`status status-${u.status === 'active' ? 'online' : 'offline'}`}>
          <span className="dot" aria-hidden />
          {t(`users.${u.status}`)}
        </span>
      </td>
      <td className="muted hide-mobile">
        {u.last_login_at ? formatRelative(u.last_login_at) : t('common.never')}
      </td>
    </tr>
  );
  const selected = drawer && drawer !== 'new' ? users.data?.items.find((u) => u.id === drawer.id) : undefined;

  return (
    <div className="page">
      <PageHeader
        title={clientUser ? t('users.titleClient') : t('users.title')}
        subtitle={users.data && t('users.summary', { count: users.data.items.length })}
        actions={
          canCreate && (
            <button type="button" className="btn btn-primary" onClick={() => setDrawer('new')}>
              <UserPlus size={16} /> {t('users.new')}
            </button>
          )
        }
      />
      <div className={`with-drawer${drawer ? ' open' : ''}`}>
        <div className="grid" style={{ gap: 'var(--space-4)' }}>
          <Card bodyClass="">
            <div className="card-body toolbar" style={{ borderBottom: '1px solid var(--color-border)' }}>
              <span className="input-affix" style={{ flex: '1 1 220px' }}>
                <Search size={15} className="muted" style={{ position: 'absolute', left: 10, top: 9 }} />
                <input
                  className="input"
                  style={{ paddingLeft: 32, width: '100%' }}
                  placeholder={t('common.search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label={t('common.search')}
                />
              </span>
              {!clientUser && (
                <div className="users-tabs">
                  <Segmented
                    label={t('users.tabs.label')}
                    value={tab}
                    onChange={setTab}
                    options={[
                      { value: 'clients', label: `${t('users.tabs.clients')} (${counts.clients})` },
                      { value: 'platform', label: `${t('users.tabs.platform')} (${counts.platform})` },
                    ]}
                  />
                </div>
              )}
              {!clientUser && tab === 'clients' && (
                <select
                  className="input"
                  value={client}
                  onChange={(e) => setClient(e.target.value)}
                  aria-label={t('users.client')}
                >
                  <option value="">{t('users.allClients')}</option>
                  {tree.roots.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}
              <Segmented
                label={t('common.status')}
                value={status}
                onChange={setStatus}
                options={[
                  { value: 'all', label: t('common.all') },
                  { value: 'active', label: t('users.active') },
                  { value: 'disabled', label: t('users.disabled') },
                ]}
              />
            </div>
            {users.isError ? (
              <div className="card-body">
                <ErrorNote error={users.error} />
              </div>
            ) : list.length === 0 && !users.isLoading ? (
              <div className="empty-state">
                <UsersIcon size={32} className="muted" />
                <span className="muted">{t('users.empty')}</span>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('users.user')}</th>
                      <th>{clientUser || tab === 'clients' ? t('users.role') : t('users.rolesAndAreas')}</th>
                      <th>{t('common.status')}</th>
                      <th className="hide-mobile">{t('users.lastLogin')}</th>
                    </tr>
                  </thead>
                  {tab === 'platform' && !clientUser ? (
                    <tbody>{list.map((u) => userRow(u, true))}</tbody>
                  ) : (
                    groups.map((g) => (
                      <tbody key={g.client?.id ?? g.name} className="user-group">
                        {!clientUser && (
                          <tr className="user-group-head">
                            <td colSpan={4}>
                              <Building2 size={15} className="muted" aria-hidden />
                              <strong>{g.name}</strong>
                              {g.client?.county && <span className="muted">· {g.client.county}</span>}
                              <span className="muted small">
                                {t('users.groupCount', { count: g.users.length })}
                              </span>
                            </td>
                          </tr>
                        )}
                        {g.users.map((u) => userRow(u, false))}
                      </tbody>
                    ))
                  )}
                </table>
              </div>
            )}
          </Card>
          {/* Client accounts only manage their own operators: the role catalogue is not relevant to them. */}
          {!clientUser && <RolesCard roles={roles.data?.items ?? []} />}
        </div>
        {drawer && (drawer === 'new' || selected) && (
          <UserDrawer
            key={drawer === 'new' ? 'new' : drawer.id}
            user={selected}
            roles={roles.data?.items ?? []}
            clients={tree.roots.filter((c) => c.kind === 'organization')}
            onClose={() => setDrawer(null)}
            created={drawer !== 'new' && drawer.created}
            onCreated={(u) => setDrawer({ id: u.id, created: true })}
          />
        )}
      </div>
    </div>
  );
}

function AssignmentChips({ user, withClient }: { user: User; withClient: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="chips">
      {user.assignments.map((a) => (
        <span key={`${a.role}-${a.site_id ?? '*'}`} className={`chip role-badge role-${a.role}`}>
          <span className="chip-role">{t(`roles.${a.role}.name`, { defaultValue: a.role })}</span>
          {withClient && (
            <span className="muted">· {a.site_id ? (a.site_name ?? '—') : t('users.allClients')}</span>
          )}
          {a.role === 'AUTHORITY_OBSERVER' && a.site_id && (
            <span
              className={a.projects.length ? 'muted' : 'role-no-projects'}
              title={a.projects.map((x) => x.name).join(', ')}
            >
              ·{' '}
              {a.projects.length
                ? t('users.projects.chip', { n: a.projects.length })
                : t('users.projects.none')}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

function RolesCard({ roles }: { roles: Role[] }) {
  const { t } = useTranslation();
  return (
    <Card
      title={
        <span className="toolbar">
          <ShieldCheck size={16} className="muted" /> {t('users.roles')}
        </span>
      }
      bodyClass=""
    >
      <div style={{ overflowX: 'auto' }}>
        <table className="table">
          <tbody>
            {roles.map((r) => (
              <tr key={r.key}>
                <td style={{ width: 180 }}>
                  <strong>{t(`roles.${r.key}.name`, { defaultValue: r.name_en })}</strong>
                </td>
                <td>
                  <span className="kind-badge">{t(`users.scopeKind.${r.scope}`)}</span>
                </td>
                <td className="muted" style={{ whiteSpace: 'normal', minWidth: 260 }}>
                  {t(`roles.${r.key}.description`, { defaultValue: '' })}
                </td>
                <td className="muted num" title={r.permissions.join('\n')}>
                  {t('users.permissions', { count: r.permissions.length })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

const generatePassword = () => {
  const alphabet = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
};

type Kind = 'client' | 'global';

/**
 * Account form (U13). Client accounts: one role (client administrator or operator) on one whole client, username
 * "<client prefix>.<name>" with the prefix imposed. Global accounts (super administrator, installer technician):
 * free username. First and last name are required, plus at least one of email and phone.
 */
function UserDrawer({
  user,
  roles,
  clients,
  onClose,
  onCreated,
  created = false,
}: {
  created?: boolean;
  user?: User;
  roles: Role[];
  clients: Site[];
  onClose: () => void;
  onCreated: (u: User) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const creating = user === undefined;
  const readOnly = !creating && !user.can_manage;
  const grantable = roles.filter((r) => r.grantable);
  const clientRoles = grantable.filter((r) => r.scope === 'site');
  const globalRoles = grantable.filter((r) => r.scope === 'global');
  const current = user?.assignments[0];
  const initialKind: Kind = current
    ? current.site_id
      ? 'client'
      : 'global'
    : clientRoles.length
      ? 'client'
      : 'global';
  const [kind, setKind] = useState<Kind>(initialKind);
  const [clientId, setClientId] = useState<string>(current?.site_id ?? clients[0]?.id ?? '');
  const [role, setRole] = useState<string>(
    current?.role ?? (initialKind === 'client' ? clientRoles.at(-1)?.key : globalRoles[0]?.key) ?? '',
  );
  // U21: an authority observer may be limited to some projects of its client (none ticked = the whole client)
  const [projectIds, setProjectIds] = useState<string[]>(current?.project_ids ?? []);
  const limitable = kind === 'client' && role === 'AUTHORITY_OBSERVER';
  const clientProjects = useQuery({
    queryKey: ['projects', clientId],
    queryFn: () => fetchProjects(clientId),
    enabled: limitable && clientId !== '',
  });
  const [form, setForm] = useState({
    name: '',
    first_name: user?.first_name ?? '',
    last_name: user?.last_name ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    locale: (user?.locale ?? 'ro') as 'en' | 'ro',
    status: user?.status ?? 'active',
    password: '',
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saved, setSaved] = useState(created);
  const [shownPassword, setShownPassword] = useState<string | null>(null);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setSaved(false);
  };
  const client = clients.find((c) => c.id === clientId);
  const prefix = kind === 'client' ? (client?.user_prefix ?? '') : '';
  const username = kind === 'client' ? `${prefix}.${form.name}` : form.name;
  const refresh = () => qc.invalidateQueries({ queryKey: USERS_KEY });
  const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));
  const roleChanged =
    !creating && (role !== current?.role || (limitable && !sameSet(projectIds, current?.project_ids ?? [])));
  const assignments = [
    {
      role,
      site_id: kind === 'client' ? clientId : null,
      ...(limitable ? { project_ids: projectIds } : {}),
    },
  ];

  const save = useMutation({
    mutationFn: async () => {
      const person = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        locale: form.locale,
      };
      if (creating) return createUser({ ...person, username, password: form.password, assignments });
      return updateUser(user.id, {
        ...person,
        status: form.status,
        ...(form.password ? { password: form.password } : {}),
        ...(roleChanged ? { assignments } : {}),
      });
    },
    onSuccess: async (u) => {
      await refresh();
      setSaved(true);
      setForm((f) => ({ ...f, password: '' }));
      setShownPassword(null);
      if (creating) onCreated(u);
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteUser(user!.id),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });

  const nameOk =
    /^[a-z0-9][a-z0-9._-]{0,40}$/.test(form.name) && (kind === 'client' || form.name.length >= 3);
  const valid =
    form.first_name.trim() !== '' &&
    form.last_name.trim() !== '' &&
    (form.email.trim() !== '' || form.phone.trim() !== '') &&
    role !== '' &&
    (kind === 'global' || clientId !== '') &&
    (!creating || (nameOk && form.password.length >= 12 && (kind === 'global' || prefix !== ''))) &&
    (form.password === '' || form.password.length >= 12);

  return (
    <Drawer
      title={creating ? t('users.new') : user.display_name}
      subtitle={creating ? t('users.newHint') : <span className="mono">{user.username}</span>}
      onClose={onClose}
      footer={
        !readOnly && (
          <>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!valid || save.isPending}
              onClick={() => save.mutate()}
            >
              {creating ? t('common.create') : t('common.save')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <span style={{ flex: 1 }} />
            {!creating && (
              <button
                type="button"
                className="btn btn-ghost btn-danger"
                onClick={() => setConfirmDelete(true)}
              >
                {t('common.delete')}
              </button>
            )}
          </>
        )
      }
    >
      {readOnly && (
        <p className="muted" style={{ margin: 0 }}>
          {t('users.readOnly')}
        </p>
      )}

      {creating && clientRoles.length > 0 && globalRoles.length > 0 && (
        <Field label={t('users.accountKind')} group>
          <Segmented<Kind>
            label={t('users.accountKind')}
            value={kind}
            onChange={(k) => {
              setKind(k);
              setRole((k === 'client' ? clientRoles.at(-1)?.key : globalRoles[0]?.key) ?? '');
            }}
            options={[
              { value: 'client', label: t('users.kindClient') },
              { value: 'global', label: t('users.kindGlobal') },
            ]}
          />
        </Field>
      )}
      {kind === 'client' && (
        <Field label={t('users.client')} hint={!creating ? t('users.clientFixed') : undefined}>
          <select
            className="input"
            value={clientId}
            disabled={!creating || clients.length < 2}
            onChange={(e) => setClientId(e.target.value)}
          >
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label={t('users.role')}>
        <select
          className="input"
          value={role}
          disabled={readOnly || (kind === 'client' ? clientRoles : globalRoles).length < 2}
          onChange={(e) => (setRole(e.target.value), setSaved(false))}
        >
          {(readOnly
            ? roles.filter((r) => r.key === role)
            : kind === 'client'
              ? clientRoles
              : globalRoles
          ).map((r) => (
            <option key={r.key} value={r.key}>
              {t(`roles.${r.key}.name`, { defaultValue: r.name_en })}
            </option>
          ))}
        </select>
      </Field>
      {limitable && (
        <fieldset className="fieldset">
          <legend>{t('users.projects.title')}</legend>
          <span className="muted small">
            {projectIds.length
              ? t('users.projects.limited', { n: projectIds.length })
              : t('users.projects.all')}
          </span>
          {(clientProjects.data?.items ?? []).map((pr) => (
            <label key={pr.id} className="check-row">
              <input
                type="checkbox"
                checked={projectIds.includes(pr.id)}
                disabled={readOnly}
                onChange={(e) => {
                  setProjectIds((ids) =>
                    e.target.checked ? [...ids, pr.id] : ids.filter((x) => x !== pr.id),
                  );
                  setSaved(false);
                }}
              />
              <span>
                {pr.name}
                <span className="muted small">
                  {' '}
                  · {t('projects.devices', { n: formatNumber(pr.devices) })}
                </span>
              </span>
            </label>
          ))}
          <ErrorNote error={clientProjects.error} />
        </fieldset>
      )}
      {creating && (
        <Field
          label={t('users.username')}
          hint={
            kind === 'client'
              ? t('users.usernameClientHint', { prefix: prefix || '…' })
              : t('users.usernameHint')
          }
        >
          <span className="input-prefix">
            {kind === 'client' && <span className="input-prefix-text mono">{prefix || '…'}.</span>}
            <input
              className="input mono"
              value={form.name}
              autoFocus
              autoComplete="off"
              maxLength={40}
              aria-label={t('users.username')}
              placeholder={kind === 'client' ? 'admin' : ''}
              onChange={(e) => set('name', e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''))}
            />
          </span>
        </Field>
      )}
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <Field label={t('users.firstName')}>
          <input
            className="input"
            value={form.first_name}
            disabled={readOnly}
            maxLength={60}
            onChange={(e) => set('first_name', e.target.value)}
          />
        </Field>
        <Field label={t('users.lastName')}>
          <input
            className="input"
            value={form.last_name}
            disabled={readOnly}
            maxLength={60}
            onChange={(e) => set('last_name', e.target.value)}
          />
        </Field>
      </div>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <Field label={t('users.email')}>
          <input
            className="input"
            type="email"
            value={form.email}
            disabled={readOnly}
            onChange={(e) => set('email', e.target.value)}
          />
        </Field>
        <Field label={t('users.phone')}>
          <input
            className="input"
            type="tel"
            value={form.phone}
            disabled={readOnly}
            placeholder="+40 7xx xxx xxx"
            onChange={(e) => set('phone', e.target.value)}
          />
        </Field>
      </div>
      <span className="field-hint" style={{ marginTop: 'calc(-1 * var(--space-2))' }}>
        {t('users.contactHint')}
      </span>
      <div className="toolbar" style={{ gap: 'var(--space-4)' }}>
        <Field label={t('settings.language')} group>
          <Segmented<'en' | 'ro'>
            label={t('settings.language')}
            value={form.locale}
            onChange={(v) => !readOnly && set('locale', v)}
            options={[
              { value: 'ro', label: 'Română' },
              { value: 'en', label: 'English' },
            ]}
          />
        </Field>
        {!creating && (
          <Field label={t('common.status')} group>
            <Segmented<'active' | 'disabled'>
              label={t('common.status')}
              value={form.status}
              onChange={(v) => !readOnly && set('status', v)}
              options={[
                { value: 'active', label: t('users.active') },
                { value: 'disabled', label: t('users.disabled') },
              ]}
            />
          </Field>
        )}
      </div>
      {!readOnly && (
        <Field
          label={creating ? t('users.password') : t('users.newPassword')}
          hint={shownPassword ? t('users.passwordShown') : t('users.passwordHint')}
        >
          <span className="toolbar" style={{ flexWrap: 'nowrap' }}>
            <input
              className="input mono"
              type={shownPassword ? 'text' : 'password'}
              value={form.password}
              autoComplete="new-password"
              style={{ flex: 1 }}
              onChange={(e) => (set('password', e.target.value), setShownPassword(null))}
            />
            <button
              type="button"
              className="btn"
              title={t('users.generate')}
              onClick={() => {
                const pw = generatePassword();
                set('password', pw);
                setShownPassword(pw);
              }}
            >
              <KeyRound size={15} /> <span className="hide-mobile">{t('users.generate')}</span>
            </button>
          </span>
        </Field>
      )}
      {!creating && <MobilePairing user={user} onChanged={refresh} />}
      {!creating && (
        <dl className="kv">
          <dt>{t('users.created')}</dt>
          <dd>{formatRelative(user.created_at)}</dd>
          <dt>{t('users.lastLogin')}</dt>
          <dd>{user.last_login_at ? formatRelative(user.last_login_at) : t('common.never')}</dd>
        </dl>
      )}
      {saved && <div className="form-ok">{t('common.saved')}</div>}
      <ErrorNote error={save.error ?? remove.error} />
      {confirmDelete && user && (
        <div className="confirm-box">
          <strong>{t('users.deleteConfirm', { name: user.display_name })}</strong>
          <span className="muted">{t('users.deleteHint')}</span>
          <div className="toolbar">
            <button
              type="button"
              className="btn btn-danger-solid"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {t('common.delete')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmDelete(false)}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </Drawer>
  );
}
